import { HeaderAvatar } from '@/components/layout/HeaderAvatar';
import { HeaderLogo } from '@/components/layout/HeaderLogo';
import { HEADER_SURFACE_CLASS } from '@/components/layout/headerSurface';
import { SHELL_GUTTER_STYLE } from '@/components/layout/shellGeometry';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantContext } from '@/contexts/TenantContext';
import { useUnreadCount } from '@/hooks';
import { cn } from '@/utils';
import { Bell, CircleUserRound, LifeBuoy, LogOut, Menu, Settings } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { currentTenant, currentUser } = useTenantContext();
  const { logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count ?? 0;

  return (
    <header
      className={cn(
        // The 80 px logo bar on laptops and desktops (Estevan 10-15: team invites' 52 px row is undone
        // there; the sidebar fits under it by its window-height rule); a phone keeps the compact bar.
        'h-[52px] lg:h-20 border-b border-white/[0.03] shadow-[0_1px_0_0_rgba(255,255,255,0.04),0_4px_12px_-2px_rgba(0,0,0,0.3),0_8px_24px_-4px_rgba(0,0,0,0.2)] flex items-center justify-between px-4 lg:px-[var(--shell-gutter)] relative z-10',
        HEADER_SURFACE_CLASS,
      )}
      // from lg the logo sits the shell gutter in from the window's edge, the sidebar's line (shellGeometry.ts)
      style={SHELL_GUTTER_STYLE}
    >
      {/* Left: hamburger + logo */}
      <div className="flex items-center gap-3">
        {/* Mobile menu button */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Logo — links to home (the workspace is named in the avatar block, top right: Estevan 10-15) */}
        <Link to="/" aria-label="Go to home">
          <HeaderLogo />
        </Link>
      </div>

      {/* Right: notifications + user / tenant */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Notifications */}
        <button
          onClick={() => navigate('/notifications')}
          className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors relative"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-brand-500 rounded-full" />
          )}
        </button>

        {/* Divider */}
        <div className="hidden sm:block w-px h-8 bg-white/10" />

        {/* User dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu((v) => !v)}
            onBlur={() => setTimeout(() => setShowMenu(false), 150)}
            aria-label={`Account menu for ${currentUser.name}`}
            aria-haspopup="menu"
            aria-expanded={showMenu}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            {/* The user's own photo (C-27), else the person icon */}
            <HeaderAvatar />
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-white leading-tight truncate max-w-[140px]">
                {currentTenant.name}
              </p>
              <p className="text-xs text-gray-400 leading-tight truncate max-w-[140px]">
                {currentUser.name}
              </p>
            </div>
          </button>

          {/* Dropdown menu */}
          {showMenu && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-48 py-1 bg-neutral-800/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-xl"
            >
              {/* Your own profile (photo, account) for everyone: never an employee page, which
                  dead-ends for anyone not on the customer's employee list (Estevan 10-03). */}
              <Link
                to="/profile"
                role="menuitem"
                onClick={() => setShowMenu(false)}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              >
                <CircleUserRound className="h-4 w-4" />
                Profile
              </Link>
              <Link
                to="/settings"
                role="menuitem"
                onClick={() => setShowMenu(false)}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Settings className="h-4 w-4" />
                Settings
              </Link>
              <a
                href="mailto:estevan.mccalley@gmail.com?subject=AskWingo%20Support%20Request"
                role="menuitem"
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              >
                <LifeBuoy className="h-4 w-4" />
                Contact Support
              </a>
              <button
                onClick={logout}
                role="menuitem"
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
